import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  BarChart3,
  Users,
  Zap,
  Clock,
  Globe,
  Shield,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Layers,
  Target,
  TrendingUp,
  MessageSquare,
  Image,
  Video,
  Hash,
  Bell,
  FileText,
  Palette
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const FeaturesPage = () => {
  const features = [
    {
      icon: Calendar,
      title: 'Content Calendar',
      description: 'Visual drag-and-drop calendar to plan, schedule, and organize your social media content across all platforms.',
      highlights: ['Drag & drop scheduling', 'Multi-platform view', 'Bulk scheduling', 'Auto-publishing']
    },
    {
      icon: Sparkles,
      title: 'AI Content Tools',
      description: 'Leverage AI to generate captions, hashtags, and content ideas that resonate with your audience.',
      highlights: ['AI caption generation', 'Smart hashtag suggestions', 'Content ideas', 'Video generation']
    },
    {
      icon: BarChart3,
      title: 'Analytics & Insights',
      description: 'Track performance across all platforms with detailed analytics and actionable insights.',
      highlights: ['Cross-platform analytics', 'Post performance', 'Audience insights', 'Custom reports']
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Work together seamlessly with role-based permissions, approval workflows, and activity tracking.',
      highlights: ['Role-based access', 'Approval workflows', 'Comments & feedback', 'Activity logs']
    },
    {
      icon: Globe,
      title: 'Multi-Platform Publishing',
      description: 'Connect and publish to 11+ social platforms from a single dashboard.',
      highlights: ['Facebook & Instagram', 'LinkedIn & Twitter', 'TikTok & YouTube', 'Pinterest & more']
    },
    {
      icon: Image,
      title: 'Media Library',
      description: 'Centralized asset management with cloud storage, organization, and quick access.',
      highlights: ['Cloud storage', 'Image editing', 'Brand assets', 'Quick search']
    },
    {
      icon: Palette,
      title: 'Brand Guidelines',
      description: 'Define and enforce brand consistency with colors, fonts, voice, and content rules.',
      highlights: ['Color palettes', 'Typography rules', 'Voice & tone', 'Content validation']
    },
    {
      icon: Target,
      title: 'Competitor Analysis',
      description: 'Track competitors, benchmark performance, and identify content opportunities.',
      highlights: ['Competitor tracking', 'Content analysis', 'Gap identification', 'Strategy insights']
    },
    {
      icon: Bell,
      title: 'Smart Notifications',
      description: 'Stay informed with in-app, email, and push notifications for important updates.',
      highlights: ['Push notifications', 'Email alerts', 'Approval reminders', 'Performance alerts']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-emerald-600 font-medium">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/integrations" className="text-slate-600 hover:text-slate-900">Integrations</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-emerald-100 text-emerald-700 mb-4">Features</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Everything You Need to
            <span className="text-emerald-500"> Master Social Media</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Powerful tools designed to help you plan, create, publish, and analyze your social media content at scale.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full border-slate-100 hover:border-emerald-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 mb-4">{feature.description}</p>
                    <ul className="space-y-2">
                      {feature.highlights.map(highlight => (
                        <li key={highlight} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Transform Your Social Media?</h2>
          <p className="text-emerald-100 mb-8">Start your 14-day free trial. No credit card required.</p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
              Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
